/**
 * Sonido del juego, sintetizado con WebAudio.
 *
 * No hay ficheros de audio y es a propósito. El sonido que de verdad hace falta
 * aquí es el **tictac de la rueda**, y ese no se puede grabar: tiene que ir
 * frenando, y cuánto dura depende de la tirada. Una vez montado el sintetizador
 * para eso, el resto de avisos (acierto, fallo, quiebra, panel resuelto) salen de
 * lo mismo con cuatro notas, sin arrastrar megas de audio ni licencias.
 *
 * Regla de accesibilidad que manda sobre todo lo demás: **el sonido es un apoyo,
 * nunca la vía principal**. Todo lo importante se dice con palabras en la región
 * de anuncios; estos efectos solo la acompañan. Por eso son cortos, van a volumen
 * moderado para no tapar al lector de pantalla, y se pueden silenciar.
 */

/** Cada aviso con sonido propio. */
export type Cue =
  /** Una letra que sí estaba. */
  | 'letterHit'
  /** Una letra que no estaba. */
  | 'letterMiss'
  /** Vocal descubierta. */
  | 'vowel'
  /** Quiebra: se va todo. */
  | 'bankrupt'
  /** Pierde turno, sin perder dinero. */
  | 'loseTurn'
  /** Algo bueno que se guarda: comodín, sobre, por dos, bote, exprés. */
  | 'prize'
  /** Panel resuelto. */
  | 'solved'
  /** Fin de la partida. */
  | 'gameOver'
  /** Empieza una ronda. */
  | 'roundStart'
  /** Le toca a alguien. */
  | 'turn'
  /** Alguien entra en la sala. */
  | 'join';

/** Clave en localStorage para recordar si el jugador silenció los sonidos. */
const MUTED_KEY = 'ruleta-muted';

/** Volumen general, por debajo del máximo para no competir con la voz. */
const MASTER_VOLUME = 0.28;

/** Una nota de una melodía: frecuencia en hercios y duración en segundos. */
interface Note {
  hz: number;
  seconds: number;
}

/** Melodía de cada aviso, en el orden en que suenan sus notas. */
const CUES: Record<Cue, { notes: Note[]; wave: OscillatorType }> = {
  // Dos notas hacia arriba: la señal de que algo ha salido bien.
  letterHit: { wave: 'triangle', notes: [{ hz: 660, seconds: 0.09 }, { hz: 990, seconds: 0.12 }] },
  // Hacia abajo, corta y sin dramatismo: fallar es lo normal en este juego.
  letterMiss: { wave: 'sine', notes: [{ hz: 420, seconds: 0.1 }, { hz: 300, seconds: 0.16 }] },
  vowel: { wave: 'triangle', notes: [{ hz: 780, seconds: 0.12 }] },
  // Grave y fea, que es lo que toca.
  bankrupt: { wave: 'sawtooth', notes: [{ hz: 160, seconds: 0.18 }, { hz: 90, seconds: 0.42 }] },
  loseTurn: { wave: 'sine', notes: [{ hz: 260, seconds: 0.12 }, { hz: 220, seconds: 0.16 }] },
  prize: {
    wave: 'triangle',
    notes: [{ hz: 880, seconds: 0.07 }, { hz: 1175, seconds: 0.07 }, { hz: 1568, seconds: 0.16 }],
  },
  solved: {
    wave: 'triangle',
    notes: [
      { hz: 523, seconds: 0.11 },
      { hz: 659, seconds: 0.11 },
      { hz: 784, seconds: 0.11 },
      { hz: 1047, seconds: 0.3 },
    ],
  },
  gameOver: {
    wave: 'triangle',
    notes: [{ hz: 784, seconds: 0.16 }, { hz: 1047, seconds: 0.16 }, { hz: 1319, seconds: 0.4 }],
  },
  roundStart: { wave: 'triangle', notes: [{ hz: 440, seconds: 0.1 }, { hz: 659, seconds: 0.2 }] },
  turn: { wave: 'sine', notes: [{ hz: 560, seconds: 0.09 }] },
  join: { wave: 'sine', notes: [{ hz: 700, seconds: 0.1 }] },
};

/** Cuánto dura por defecto el tictac de una tirada. */
const SPIN_SECONDS = 1.1;
/** Separación del primer tic; a partir de ahí la rueda va frenando. */
const FIRST_TICK_SECONDS = 0.04;
/** Cuánto se alarga cada hueco respecto al anterior. Es todo el efecto de frenada. */
const TICK_SLOWDOWN = 1.13;

export class SoundEngine {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private muted: boolean;
  /**
   * Instante en que acaba el tictac que esté sonando.
   *
   * Lo que pasa por caer en una casilla llega del servidor de golpe, pero suena
   * después: si el «quiebra» sonara encima de los tics, la rueda no llegaría a
   * pararse nunca en el oído del jugador.
   */
  private spinEndsAt = 0;
  /** Tics ya programados, para poder cortarlos si la rueda se para antes. */
  private spinTicks: (OscillatorNode | null)[] = [];

  constructor() {
    this.muted = localStorage.getItem(MUTED_KEY) === 'true';
  }

  /** true si los sonidos están silenciados. */
  get isMuted(): boolean {
    return this.muted;
  }

  /**
   * @brief Cambia el estado de silencio y lo recuerda.
   * @return El nuevo estado (true si queda silenciado).
   */
  toggleMuted(): boolean {
    this.muted = !this.muted;
    localStorage.setItem(MUTED_KEY, String(this.muted));
    return this.muted;
  }

  /**
   * @brief Prepara el audio; hay que llamarlo desde un gesto del jugador.
   *
   * Los navegadores no dejan sonar nada hasta que la persona ha tocado la
   * página. Se llama en el primer clic o tecla y no pasa nada por repetirlo.
   */
  unlock(): void {
    const context = this.ensureContext();
    if (context && context.state === 'suspended') void context.resume();
  }

  /**
   * @brief Reproduce un aviso.
   * @param cue Aviso a sonar.
   */
  play(cue: Cue): void {
    const context = this.ensureContext();
    if (!context || this.muted) return;
    const { notes, wave } = CUES[cue];
    let at = Math.max(context.currentTime, this.spinEndsAt);
    for (const note of notes) {
      this.tone(note.hz, at, note.seconds, wave);
      at += note.seconds;
    }
  }

  /**
   * @brief El tictac de la rueda: rápido al principio y frenando hasta parar.
   *
   * Es lo único que no podría ser un fichero de audio: el frenado es lo que
   * cuenta que la rueda se está parando, y ahí está la tensión de la tirada.
   *
   * @param seconds Cuánto dura la tirada.
   */
  spin(seconds = SPIN_SECONDS): void {
    const context = this.ensureContext();
    if (!context || this.muted) return;
    this.stopSpin();
    let at = context.currentTime;
    let gap = FIRST_TICK_SECONDS;
    const end = at + seconds;
    while (at < end) {
      this.spinTicks.push(this.tick(at));
      at += gap;
      gap *= TICK_SLOWDOWN;
    }
    this.spinEndsAt = at;
  }

  /**
   * @brief Corta el tictac que estuviera sonando.
   *
   * Hace falta en el modo de parar la rueda a mano: los tics se programan de
   * golpe al lanzarla, así que si el jugador la para antes seguirían sonando
   * solos y la rueda se oiría girando después de haberse parado.
   */
  stopSpin(): void {
    const context = this.context;
    if (!context) return;
    for (const tick of this.spinTicks) {
      try {
        tick?.stop(context.currentTime);
      } catch {
        /* ya había terminado por su cuenta */
      }
    }
    this.spinTicks = [];
    this.spinEndsAt = context.currentTime;
  }

  /** Un tic seco: el golpe de la aguja contra la casilla. */
  private tick(at: number): OscillatorNode | null {
    return this.tone(1400, at, 0.03, 'square', 0.5);
  }

  /**
   * Una nota con su envolvente. El ataque no es instantáneo y el final baja a
   * cero: un corte en seco suena a chasquido y molesta al oído que está atento.
   */
  private tone(hz: number, at: number, seconds: number, wave: OscillatorType, level = 1): OscillatorNode | null {
    const context = this.context;
    const master = this.master;
    if (!context || !master) return null;
    const oscillator = context.createOscillator();
    const envelope = context.createGain();
    oscillator.type = wave;
    oscillator.frequency.setValueAtTime(hz, at);
    envelope.gain.setValueAtTime(0, at);
    envelope.gain.linearRampToValueAtTime(level, at + 0.008);
    envelope.gain.exponentialRampToValueAtTime(0.0001, at + seconds);
    oscillator.connect(envelope).connect(master);
    oscillator.start(at);
    oscillator.stop(at + seconds + 0.02);
    return oscillator;
  }

  /** Crea el contexto de audio la primera vez que hace falta. */
  private ensureContext(): AudioContext | null {
    if (this.context) return this.context;
    // Navegador sin WebAudio: el juego se sigue jugando entero, solo que callado.
    if (typeof AudioContext === 'undefined') return null;
    this.context = new AudioContext();
    this.master = this.context.createGain();
    this.master.gain.value = MASTER_VOLUME;
    this.master.connect(this.context.destination);
    return this.context;
  }
}
